import React from "react";
import { IoCodeSlashOutline, IoAccessibilityOutline } from "react-icons/io5";
import PropTypes from "prop-types";
import { AiOutlineApi } from "react-icons/ai";
import { BsWindowFullscreen, BsSpeedometer2 } from "react-icons/bs";
import { PiChartLineLight } from "react-icons/pi";
import { BiDevices } from "react-icons/bi";

import { differenceInMinutes, differenceInHours, differenceInDays, differenceInSeconds, formatDistanceStrict } from "date-fns";

import DOMPurify from "dompurify";
import parse from "html-react-parser";
import MD5 from 'crypto-js/md5';

import PopStaticCode from "../../components/cards/shared/popupExplanations/PopStaticCode";
import PopApiIntegration from "../../components/cards/shared/popupExplanations/PopAPIIntegration";
import PopFunctionalUI from "../../components/cards/shared/popupExplanations/PopFunctionalUI";
import PopPerformance from "../../components/cards/shared/popupExplanations/PopPerformance";
import PopAccessibility from "../../components/cards/shared/popupExplanations/PopAccessibility";
import PopCompatibility from "../../components/cards/shared/popupExplanations/PopCompatibility";

import { IS_DEBUG } from '@home/config';

//TODO - Move all colour related utilities to ColourUtils.js

//PRIORITY
// Priority Weights
export const priorityWeights = {
  SMOKE: 10,
  P1: 9,
  P2: 8,
  P3: 7,
  P4: 6,
  P5: 5,
  P6: 4,
  P7: 3,
  HIGH: 3,
  MEDIUM: 2,
  LOW: 1,
};

// Mappings of priority to color for use in charts etc
export const priorityColourMap = {
  SMOKE: "red",
  HIGH: "pink",
  P1: "pink",
  MEDIUM: "orange",
  P2: "orange",
  LOW: "yellow",
  P3: "yellow",
  P4: "blue",
};

// Mappings of priority to colour class names for use in UI components with text on the background colour i.e. Chips etc.
export const priorityColourClassNames = {
  SMOKE: "bg-red-500 text-white",
  HIGH: "bg-pink-500 text-black",
  P1: "bg-pink-500 text-black",
  MEDIUM: "bg-orange-500 text-black",
  P2: "bg-orange-500 text-black",
  P3: "bg-yellow-500 text-black",
  LOW: "bg-yellow-500 text-black",
  P4: "bg-blue-500 text-white",
};

export const BASE_COLOURS = ['gray','red','green','yellow','purple','amber','orange','lime','teal','cyan','blue','indigo','pink'];
export const TREMOR_COLOUR_NAMES = [
  // First cycle: All base colours (indexes 0-12)
  ...BASE_COLOURS,
  // Second cycle: All 300 shades (indexes 13-25)
  ...BASE_COLOURS.map(colour => `${colour}-300`),
  // Third cycle: All 700 shades (indexes 26-38)
  ...BASE_COLOURS.map(colour => `${colour}-700`)
];

// Function to get severity colour
// If the severity isn't set, potentially because the item did not have a severity value, default to blue
export const getPriorityColour = (priority) => {
  return priorityColourMap[priority.toUpperCase()] || "blue";
};

export const getPriorityColourClassNames = (priority) => {
  return priorityColourClassNames[priority?.toUpperCase()] || "bg-red-100 text-black";
};

// Function to get priority weight
// If the priority isn't set, potentially because the item did not have a priority tag, default to 1
export const getPriorityWeight = (priority) => {

  // Check if the priority is defined and has a valid value in the mapping
  if (priority && priorityWeights.hasOwnProperty(priority.toUpperCase())) {
    return priorityWeights[priority.toUpperCase()];
  }
  // Return the default weight if priority is undefined, null, or not found in the mapping
  return 1;
};

//END PRIORITY

// Function to process tags from SauceLabs API and return a flattened object
export const processTags = (tags) => {
  const flattenedTags = {};
  tags.forEach((tag) => {
    const [tagName, tagValue] = tag.split(":");

    if (!tagName || !tagValue) return;

    const formattedTagValue = tagValue.replaceAll('-', " ");

    if (tagName && tagValue) {
      flattenedTags[tagName.toLowerCase()] = formattedTagValue;
    }
  });
  return flattenedTags;
};

// Helper function to convert a string to camel case
export const toCamelCase = (str) => {
  return str
    .toLowerCase()
    .split(/[^a-zA-Z0-9]+/) // Split the string by non-alphanumeric characters
    .map((word, index) => {
      if (index === 0) return word; // First word should remain lowercase
      return word.charAt(0).toUpperCase() + word.slice(1); // Capitalise the first character of each subsequent word
    })
    .join("");
};

// Helper function to convert a string to sentence case (first letter capital, rest lowercase)
export const toSentenceCase = (str) => {

  if (!str) {
    return '';
  }

  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

// STATIC CODE ANALYSIS

// Mappings of severity to color for use in charts etc
export const severityColourMap = {
  BLOCKER: "red",
  CRITICAL: "pink",
  MAJOR: "orange",
  MINOR: "yellow",
  INFO: "blue",
};

// Mappings of severity to colour class names for use in UI components with text on the background colour i.e. Chips etc.
export const severityColourClassNames = {
  BLOCKER: "bg-red-500 text-white",
  CRITICAL: "bg-pink-500 text-black",
  MAJOR: "bg-orange-500 text-black",
  MINOR: "bg-yellow-500 text-black",
  INFO: "bg-blue-500 text-white",
}

export const severityHexMap = {
  BLOCKER: "#e91b0c",   // red-500
  CRITICAL: "#de5d70",  // pink-500
  MAJOR: "#ff9800",     // orange-500
  MINOR: "#ffcf01",     // yellow-500
  INFO: "#1D3154",      // blue-500
};

// Function to get severity colour
// If the severity isn't set, potentially because the item did not have a severity value, default to blue
export const getSeverityColour = (severity) => {
  return severityColourMap[severity] || "blue";
};

export const getSeverityColourClassNames = (severity) => {
  return severityColourClassNames[severity] || "bg-blue-500 text-white";
};

export const getSeverityHexColour = (severity) => {
  return severityHexMap[severity] || "#3b82f6"; // Default to blue-500
};

//Analyse issues and return a rating
export const evaluateCategorySeverityRating = (issues) => {

  if (issues.some((issue) => issue.severity === "BLOCKER")) {
    return 5;
  }

  if (issues.some((issue) => issue.severity === "CRITICAL")) {
    return 4;
  }

  if (issues.some((issue) => issue.severity === "MAJOR")) {
    return 3;
  }

  if (issues.some((issue) => issue.severity === "MINOR")) {
    return 2;
  }

  return 1; // Default for no issues or only INFO severity issues
};

//Assign a numeric value to each severity
const severityRanking = {
  BLOCKER: 1,
  CRITICAL: 2,
  MAJOR: 3,
  MINOR: 4,
  INFO: 5,
};

//Sort issues by severity
export const sortIssuesBySeverity = (a, b) => {
  return severityRanking[a.severity] - severityRanking[b.severity];
};

// Mappings of severity to colour for use in charts etc
export const accessibilityViolationColours = {
  critical: "red",
  serious: "orange",
  moderate: "yellow",
  minor: "gray",
};

// Mappings of severity to colour class names for use in UI components with text on the background colour i.e. Chips etc.
// Chip classes per test outcome (PassFailChip). Keys are lower case with spaces as underscores,
// so 'Not Executed' and 'not_executed' both land. Every pair clears WCAG AA; PassFailChip.test.js measures them.
export const passFailChipClassNames = {
  passed: 'bg-green-800 text-white',
  failed: 'bg-red-500 text-white',
  error: 'bg-pink-500 text-black',
  errored: 'bg-pink-500 text-black',
  skipped: 'bg-orange-500 text-black',
  not_executed: 'bg-orange-500 text-black',
  not_found: 'bg-orange-500 text-black',
  other: 'bg-otherGray text-black',
};

export const getPassFailChipClassNames = (result) =>
  passFailChipClassNames[`${result ?? ''}`.trim().toLowerCase().replaceAll(/\s+/g, '_')] || passFailChipClassNames.other;

export const accessibilityViolationColourClassNames = {
  critical: "bg-red-500 text-white",
  serious: "bg-orange-500 text-black",
  moderate: "bg-yellow-500 text-black",
  minor: "bg-gray-500 text-black"
}

// Function to get violation colour
// If the severity isn't set, potentially because the item did not have a severity value, default to grey
export const getAccessibilityViolationColours = (severity) => {
  return accessibilityViolationColours[severity] || "gray";
};

export const getAccessibilityColourClassNames = (severity) => {
  return accessibilityViolationColourClassNames[severity] || "bg-blue-500 text-white";
};

export const TEST_TYPE_IDS = {
  STATIC_CODE: 1,
  API: 2,
  FUI: 3,
  PERFORMANCE: 4,
  ACCESSIBILITY: 5,
  COMPATIBILITY: 6,
};

// Feature Details Mapping
export const featureDetails = {
  0: {
    icon: PiChartLineLight,
    title: "Overview",
    description:
      "Get a high-level overview of your application and its test results.",
  },
  1: {
    icon: IoCodeSlashOutline,
    title: "Static Code Analysis",
    description:
      "Analyse your codebase for potential vulnerabilities and code quality issues.",
    popover: PopStaticCode,
  },
  2: {
    icon: AiOutlineApi,
    title: "API Integration",
    description:
      "Manage and test the integration points between your application and external APIs.",
    popover: PopApiIntegration,

    //Removed https://app.eu-central-1.saucelabs.com/tests/{id} from detailLink to prevent the link appearing as
    //it is not supported currently in Azure DevOps
    detailLink: "",
  },
  3: {
    icon: BsWindowFullscreen,
    title: "Functional UI",
    description:
      "Test and ensure that the user interface behaves as expected across different scenarios.",
    popover: PopFunctionalUI,

    //Removed https://app.eu-central-1.saucelabs.com/tests/{id} from detailLink to prevent the link appearing as
    //it is not supported currently in Azure DevOps
    detailLink: "",
  },
  4: {
    icon: BsSpeedometer2,
    title: "Performance",
    description:
      "Monitor and optimise the performance of your application under various conditions.",
    popover: PopPerformance,
  },
  5: {
    icon: IoAccessibilityOutline,
    title: "Accessibility",
    description:
      "Evaluate your application for accessibility compliance and identify potential improvements.",
    popover: PopAccessibility,

    //Removed https://app.eu-central-1.saucelabs.com/tests/{id} from detailLink to prevent the link appearing as
    //it is not supported currently in Azure DevOps
    detailLink: "",
  },
  6: {
    icon: BiDevices,
    title: "Compatibility",
    description:
      "Ensure that your application is compatible with multiple devices and browsers.",
    popover: PopCompatibility,

    //Removed https://app.eu-central-1.saucelabs.com/tests/{id} from detailLink to prevent the link appearing as
    //it is not supported currently in Azure DevOps
    detailLink: "",
  },
};

export const PROVIDER_PLATFORMS = {
  MOBILE: 'mobile',
  WEB: 'web',
  BOTH: 'both',
};

export const testResultStatus = {
  success: "Test has completed successfully.",
  error: "The test has failed to complete.",
  pending: "Testing is in progress.",
  ready_to_collect: "Testing has completed and results are being collected.",
};

export const getStatusDescription = (status) => {
  const statusDetails = {
    pending: { description: "In progress", colour: "bg-yellow-500 text-black" },
    data_collecting: { description: "Collecting data", colour: "bg-yellow-500 text-black" },
    long_running: { description: "Long running", colour: "bg-yellow-500 text-black" },
    success: { description: "Completed", colour: "bg-green-800 text-white" },
    error: { description: "Failed", colour: "bg-red-500 text-white" },
  };

  return statusDetails[status] || { description: "Unknown", colour: "bg-gray-500 text-white" };
};

export const normaliseData = (data, fields) => {
  const normalisedData = {};
  fields.forEach((field) => {
    normalisedData[field] = data[field] || ""; // Normalise null or undefined to ''
  });
  return normalisedData;
};

/**
 * Converts a given date to the user's local time zone.
 * When forDisplay is true, the returned string is formatted for human display using the user's locale.
 * Otherwise it is returned in a fixed "yyyy-MM-ddTHH:mm" or "yyyy-MM-ddTHH:mm:ss" format (without timezone offset)
 * appropriate for HTML datetime-local inputs.
 *
 * @param {string|Date} pDate - The date to be converted. Can be a Date object or a date string.
 * @param {boolean} [forDisplay=false] - If true, the date is formatted for display according to the user's locale.
 * @param {boolean} [includeTime=false] - Whether to include the time (hours and minutes, and optionally seconds).
 * @param {boolean} [includeSeconds=true] - When includeTime is true, whether to also include seconds.
 * @returns {string} The formatted date string.
 */
export const convertDateToLocal = (
  pDate,
  forDisplay = false,
  includeTime = false,
  includeSeconds = true
) => {
  const userLocale = Intl.DateTimeFormat().resolvedOptions().locale;
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Basic options for the date portion.
  const options = {
    timeZone: userTimeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  };

  // If time needs to be included, add time options.
  if (includeTime) {
    const timeOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    };

    if (includeSeconds) {
      timeOptions.second = "2-digit";
    }

    Object.assign(options, timeOptions);
  }

  // Create a formatter using the above options.
  const dateFormatter = new Intl.DateTimeFormat(userLocale, options);
  const dateObj = new Date(pDate);

  if (forDisplay) {
    // Format directly for display using the user's locale.
    return dateFormatter.format(dateObj);
  } else {
    // For HTML datetime-local input, we need a controlled string like "yyyy-MM-ddTHH:mm" or with seconds.
    // Get the individual parts using formatToParts.
    const parts = dateFormatter.formatToParts(dateObj);
    const year = parts.find((part) => part.type === "year")?.value;
    const month = parts.find((part) => part.type === "month")?.value;
    const day = parts.find((part) => part.type === "day")?.value;
    let formattedDate = `${year}-${month}-${day}`;

    if (includeTime) {
      const hour = parts.find((part) => part.type === "hour")?.value;
      const minute = parts.find((part) => part.type === "minute")?.value;
      formattedDate += `T${hour}:${minute}`;

      if (includeSeconds) {
        const second = parts.find((part) => part.type === "second")?.value;
        formattedDate += `:${second}`;
      }
    }

    return formattedDate;
  }
};

export const getTimeDifference = (date1, date2) => {
  // Parse dates
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  // Calculate difference in milliseconds
  const diff = Math.abs(d2 - d1);

  // Convert to seconds, minutes, hours
  const seconds = Math.floor((diff / 1000) % 60);
  const minutes = Math.floor((diff / (1000 * 60)) % 60);
  const hours = Math.floor(diff / (1000 * 60 * 60));

  // Format with leading zeros where necessary
  const formattedSeconds = `${seconds} sec`;
  const formattedMinutes = `${minutes} min`;
  const formattedHours = `${hours} hr`;

  // Conditionally append hours if it is non-zero
  if (hours !== 0) {
    return `${formattedHours} ${formattedMinutes} ${formattedSeconds}`;
  } else if (minutes === 0) {
    return `${formattedSeconds}`;
  } else {
    return `${formattedMinutes} ${formattedSeconds}`;
  }
};

// Helper function to format singular and plural time units
const formatTimeUnit = (value, singular, plural) => {
  return `${value} ${value === 1 ? singular : plural}`;
};

/**
 * How long ago something happened. date-fns does the wording; the two ends are ours.
 *
 * Under a minute reads as "just now" rather than counting seconds, and anything a day or more old is
 * given as a date, because "9 days ago" is harder to act on than the date itself. Both bounds are
 * decided on the difference, not by inspecting the formatted string - date-fns also emits months and
 * years, and its wording is localised.
 *
 * A date in the future - clock skew between the browser and the API - lands in "just now".
 *
 * @param {string|Date|null|undefined} pDate
 * @param {Date} [now=new Date()] - Injected by tests; production always passes the real clock.
 * @returns {string} Empty string when the date is missing or unparseable.
 */
export const formatTimeAgo = (pDate, now = new Date()) => {
  if (!pDate) {
    return "";
  }

  const then = new Date(pDate);

  if (Number.isNaN(then.getTime())) {
    return "";
  }

  if (differenceInSeconds(now, then) < 60) {
    return "just now";
  }

  if (differenceInHours(now, then) >= 24) {
    return `on ${convertDateToLocal(then, true)}`;
  }

  // Floored, so the wording agrees with the two guards above - both of those truncate. Rounding is what
  // turns 59m30s into "60 minutes ago" and 23h01m into "24 hours ago".
  return formatDistanceStrict(then, now, { addSuffix: true, roundingMethod: "floor" });
};

export const formatMinutes = (minutes) => {
  // Convert minutes to days, hours, and minutes
  const days = Math.floor(minutes / 1440); // 1440 minutes in a day
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = Math.round(minutes % 60);

  const result = [];

  // Add days to the result if applicable
  if (days > 0) {
    result.push(formatTimeUnit(days, "day", "days"));
  }

  // Add hours to the result if applicable
  if (hours > 0) {
    result.push(formatTimeUnit(hours, "hour", "hours"));
  }

  // Add minutes to the result if applicable
  if (mins > 0 || minutes < 60) {
    result.push(formatTimeUnit(mins, "minute", "minutes"));
  }

  // Join the result array with spaces and return the formatted string
  return result.join(" ");
};

/**
 * Format a duration between two dates in a human-readable format. 
 * 
 * @param {Date|string} start - start datetime
 * @param {Date|string|null} [end] - optional end date. Current datetime is used if omitted, null or empty.
 * @returns {string} - The formatted duration string
 */
export const formatDuration = (start, end) => {
  const startDate = new Date(start);
  // Not a default parameter: active events send end_time as null, which a default would not catch.
  const endDate = end ? new Date(end) : new Date();
  const minutes = differenceInMinutes(endDate, startDate);

  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  const hours = differenceInHours(endDate, startDate);

  if (hours < 24) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }

  const days = differenceInDays(endDate, startDate);
  return `${days} day${days === 1 ? '' : 's'}`;
};

/**
 *  Calculate the progress of a task based on the start and end dates.
 *  
 * @param {*} start - start datetime of event
 * @param {*} end - end datetime of event
 * @param {*} now - current datetime
 * @returns {number} - The progress percentage
 */
export const calculateProgress = (start, end, now = new Date()) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  const totalDuration = differenceInMinutes(endDate, startDate);
  const elapsedDuration = differenceInMinutes(now, startDate);
  return Math.min((elapsedDuration / totalDuration) * 100, 100);
};

/**
 * Converts an ISO or local datetime string to a full ISO 8601 string
 * with the user's current offset (incl. DST), preserving local meaning.
 *
 * Handles Zulu time (Z), offset strings (+01:00), or local inputs.
 * 
 * @param {*} inputDateTime 
 * @returns {string} - The timezone-aware ISO string 
 */
export function convertInputToTimezoneAwareISO(inputDateTime) {

  if (!inputDateTime) return inputDateTime;

  const parsed = new Date(inputDateTime); // respects Z/+01:00 or assumes local

  // Convert UTC time to equivalent local wall-clock time
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  const dd = String(parsed.getDate()).padStart(2, '0');
  const hh = String(parsed.getHours()).padStart(2, '0');
  const min = String(parsed.getMinutes()).padStart(2, '0');
  const ss = String(parsed.getSeconds()).padStart(2, '0');

  // Determine the local offset at that moment
  const offsetMinutes = -parsed.getTimezoneOffset(); // e.g. -60 for BST
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const pad = (n) => String(Math.floor(Math.abs(n))).padStart(2, '0');
  const offset = `${sign}${pad(offsetMinutes / 60)}:${pad(offsetMinutes % 60)}`;

  return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}${offset}`;
}

/**
 * Sanitize HTML and convert to React elements.
 * 
 * @param {string} html - The HTML string to sanitize
 * @param {string[]} [allowedTags=['b', 'i', 'br']] - Array of allowed HTML tags. Pass empty array to strip all HTML.
 * @returns {React.ReactElement} React element containing sanitized content
 */
export const createSafeMarkup = (html, allowedTags = ["b", "i", "br"]) => {
  // First sanitise the HTML
  const sanitisedHtml = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: allowedTags,
  });

  // Parse the sanitised HTML into React nodes
  const parsedContent = parse(sanitisedHtml);

  // Wrap in a container div to ensure single element and standardise ouput
  return React.createElement("span", null, parsedContent);
};

export const isDarkMode = () => {
  return document.body.classList.contains("dark");
};

/*
 * Standardise the totals object to ensure it contains the required field names
 * and default values for any missing fields. This function is idempotent and
 * will return the object as is if it already contains the standardised fields.
 * This function handles old SauceLabs field names and new standardised field names.
 *
 * @param {object} dataObject - The object to standardise
 * @returns {object} - The standardised object
 */
export const standardiseTotals = (dataObject) => {

  if (!dataObject || typeof dataObject !== "object") {
    // Return default empty object
    return {
      Passed: 0,
      Failed: 0,
      Errors: 0,
      Others: 0,
    };
  }

  // Check if the object already contains the new standardised fields
  const hasNewFields = ["Passed", "Failed", "Errors", "Others"].every(
    (field) => field in dataObject
  );

  if (hasNewFields) {
    // Object already contains the new fields, return as is
    return dataObject;
  }

  // Define a mapping from old field names to standardised field names
  const fieldMapping = {
    // Old field names mapping to new standardised field names
    passed: "Passed",
    failed: "Failed",
    errors: "Errors",
    others: "Others",
    totalpassed: "Passed",
    totalfailed: "Failed",
    totalerrors: "Errors",
    totalotheroutcomes: "Others",
    totalothers: "Others",
  };

  const standardisedData = {};

  // Iterate over the keys in the dataObject
  Object.keys(dataObject).forEach((key) => {
    // Normalise the key to lowercase for case-insensitive comparison
    const normalisedKey = key.toLowerCase();

    // Determine the standardised key using the mapping
    const standardisedKey = fieldMapping[normalisedKey] || key;

    // Assign the value to the standardised key in the result
    standardisedData[standardisedKey] = dataObject[key];
  });

  // Ensure all standard fields are present with default values
  return {
    Passed: standardisedData.Passed || 0,
    Failed: standardisedData.Failed || 0,
    Errors: standardisedData.Errors || 0,
    Others: standardisedData.Others || 0,
    ...standardisedData, // Include any other fields
  };
};

/*
 * Create a tooltip content string for the other outcomes in the data object.
 * The outcomesObject should be an object where the keys are the outcome names
 * and the values are the counts of each outcome.
 * @param {object} outcomesObject - The object containing the outcomes and counts
 * @returns {string} - The tooltip content string
 */
export const getOtherOutcomesTooltip = (outcomesObject) => {

  if (
    !outcomesObject ||
    typeof outcomesObject !== "object" ||
    Object.keys(outcomesObject).length === 0
  ) {
    return "No other outcomes";
  }

  // Create a list of the other outcomes
  const otherOutcomesList = Object.entries(outcomesObject)
    .map(([outcome, count]) => `${outcome}: ${count}`)
    .join("\n");

  return otherOutcomesList;
};

export const sortArrayAlphabetically = (options = []) => {
  return options
    .filter((option) => option != null) // Remove null/undefined values
    .sort((aString = "", bString = "") => {
      if (aString === "Unknown") return 1; // Move "Unknown" to the end
      if (bString === "Unknown") return -1;
      return aString.localeCompare(bString, undefined, { numeric: true, sensitivity: 'base' }); // Alphabetical sort with sensitivity to embedded numbers
    });
};

// Prop Types
export const propTypes = {
  priority: PropTypes.oneOf(["HIGH", "MEDIUM", "LOW"]),
  tags: PropTypes.arrayOf(PropTypes.string),
  issues: PropTypes.arrayOf(
    PropTypes.shape({
      severity: PropTypes.oneOf([
        "BLOCKER",
        "CRITICAL",
        "MAJOR",
        "MINOR",
        "INFO",
      ]),
    })
  ),
  status: PropTypes.oneOf([
    "pending",
    "data_collecting",
    "long_running",
    "success",
    "error",
  ]),
  data: PropTypes.object,
  fields: PropTypes.arrayOf(PropTypes.string),
  pDate: PropTypes.instanceOf(Date),
  forDisplay: PropTypes.bool,
  includeTime: PropTypes.bool,
  date1: PropTypes.instanceOf(Date),
  date2: PropTypes.instanceOf(Date),
  minutes: PropTypes.number,
};

sortArrayAlphabetically.propTypes = {
  options: PropTypes.array,
};

standardiseTotals.propTypes = {
  dataObject: PropTypes.object,
};

getOtherOutcomesTooltip.propTypes = {
  outcomesObject: PropTypes.object,
};

/**
 * Create a hash of a string using MD5, after removing all whitespace.
 * This is useful for generating unique identifiers for test names, as used in event test IDs.
 * 
 * @param {string} input - test name
 * @returns {string} MD5 hash string
 */
export function hashString(input) {

  if (!input) {
    return '';
  }

  const stripped = input.replaceAll(/\s+/g, ''); // remove all whitespace
  return MD5(stripped).toString();
}

/**
 * Returns a release object extended with a `release_end` field.
 * `release_end` is the start date of the next release (if available), or now.
 * 
 * @param {Array} releases - The list of releases.
 * @param {number|string} releaseId - The ID of the current release.
 * @returns {object|null} The matched release extended with `release_end`, or null if not found. Plus a flag to indicate if it's the current release.
 */
export function getReleaseWithEndDate(releases, releaseId) {

  if (!Array.isArray(releases)) {
    return null;
  }

  // Ensure all dates are valid
  const validReleases = releases
    .filter(r => r.creation_date)
    .sort((a, b) => new Date(b.creation_date) - new Date(a.creation_date)); // newest first

  const idx = validReleases.findIndex(r => r.release_id.toString() === releaseId.toString());

  if (idx === -1) {
    return null;
  }

  const current = validReleases[idx];
  const next = validReleases[idx - 1]; // next *newer* release in the timeline

  return {
    ...current,
    isCurrentRelease: !next,
    release_end: next ? next.creation_date : null,
  };
}

/**
 * Debug function to detect duplicate keys in test item arrays. 
 * This makes it easier to identify issues with test data where unique keys are expected.
 * Should only be run in debug as it is unnecessary load in production.
 * 
 * @param {Array} items - The array of items to check for duplicates.
 * @param {Function} keyGenerator - A function that generates a unique key for each item, matching the key used in the UI.
 * @param {string} [testTypeName='Unknown'] - The name of the test type being checked, used for logging purposes.
 * @return {void} - Logs warnings to the console if duplicates are found.
 */
export function detectDuplicateKeys(items, keyGenerator, testTypeName = 'Unknown') {

  if (IS_DEBUG === 'false') {
    return; // Only run in debug mode
  }

  if (!items?.length || !keyGenerator) {
    return;
  }

  console.log(`Checking for duplicate keys in ${testTypeName}...`);
  const keyMap = new Map();
  const duplicates = [];
  
  items.forEach((item, index) => {
    const key = keyGenerator(item, index);

    if (keyMap.has(key)) {
      duplicates.push({
        key,
        testType: testTypeName,
        items: [keyMap.get(key), { ...item, arrayIndex: index }]
      });
    } else {
      keyMap.set(key, { ...item, arrayIndex: index });
    }
  });
  
  if (duplicates.length > 0) {
    console.warn(`Data Quality Issue: Duplicate keys detected in ${testTypeName}`);
    console.table(duplicates.flatMap(dup => dup.items));
  } else {
    console.log(`No duplicate keys found in ${testTypeName}`);
  }
};

/**
 * Map any positive integer ID into a Tremor compatible colour, reserving index 0 for the default.
 *
 * @param {number} id – The numeric ID to map.
 * @returns {string} Colour name from TREMOR_COLOUR_NAMES.
 */
export function colourFromId(id) {

  // Return grey for invalid IDs
  if (!Number.isInteger(id) || id <= 0) {
    return TREMOR_COLOUR_NAMES[0];
  }

// For all other event types, skip index 0 to avoid clash with "Other"
  const index = ((id - 1) % (TREMOR_COLOUR_NAMES.length - 1)) + 1; // For example (27 - 1) % 38 + 1 = 27, (40 - 1) % 38 + 1 = 2
  return TREMOR_COLOUR_NAMES[index];
}

/**
* Generic wrapper for anything with a title and numeric ID.
* 
* Special handling ensures "Other" event types always get the same colour regardless of their ID, while all other event types get consistent
* colours based on their ID without clashing with "Other".
*
* If we have an id greater than the number of colours, it will reuse one of the earlier colours.
*
* The same ID will always return the same colour.
*
 * @param {string} title – A name or category. If it matches defaultTitle, returns default colour.
 * @param {number} id – Must be a positive integer.
 * @param {string} [defaultTitle='Other'] – Title that forces default colour.
 * @returns {string}
 */
export function getEntityColour(title, id, defaultTitle = 'Other') {

  if (title === defaultTitle) {
    return TREMOR_COLOUR_NAMES[0];
  }

  return colourFromId(id);
}

export const AGE_GROUPS = {
  1: { value: '1', label: '< 1 day', color: 'green' },
  2: { value: '3', label: '< 3 days', color: 'lime' },
  3: { value: '7', label: '< 7 days', color: 'yellow' },
  4: { value: '30', label: '< 30 days', color: 'orange' },
  5: { value: '90', label: '< 90 days', color: 'pink' },
  6: { value: '999', label: '90+ days', color: 'red' }
};

export const DURATION_GROUPS = {
  1: { value: '1', label: 'Under 1 hour', color: 'green' },
  2: { value: '6', label: 'Under 6 hours', color: 'lime' },
  3: { value: '24', label: 'Under 1 day', color: 'yellow' },
  4: { value: '168', label: 'Under 1 week', color: 'orange' },
  5: { value: '999', label: 'Over 1 week', color: 'red' }
};

/**
 * Function to calculate the percentage of a value relative to a total.
 *
 * @param {number} value
 * @param {number} total
 * @returns {number}
 */
export const calculatePercentage = (value, total) => {

  if (!Number.isFinite(value) || !Number.isFinite(total) || total <= 0) {
    return 0;   // Number is valid and total is 0 or more
  }

  // Restrict to 2 decimal places
  return Math.round((value / total) * 10000) / 100;
};

/**
 * Calculate signed percentage delta from previous to current.
 * Returns null when previous value is invalid or zero.
 *
 * @param {number} currentValue
 * @param {number} previousValue
 * 
 * @returns {number|null}
 */
export const calculateDeltaPercentage = (currentValue, previousValue) => {

  if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue) || previousValue === 0) {
    return null;
  }

  const delta = ((currentValue - previousValue) / previousValue) * 100;
  return Math.round(delta * 10) / 10;
};

/**
 * Remove all non-digit characters from a string.
 *
 * @param {string} s
 * @returns {string}
 */
export const onlyDigits = (s) => s.replaceAll(/[^\d]/g, '');

/**
 * Convert a string or number to a number or null.
 *
 * @param {string|number} v
 * @returns {number|null}
 */
export const toNumOrNull = (v) => (v === '' || v == null ? null : Number(v));

/**
 * Clamp a value to a specific range.
 *
 * @param {number} value - The value to clamp.
 * @param {number} minValue - The minimum value.
 * @param {number} maxValue - The maximum value.
 * @returns {number} - The clamped value.
 */
export const clampToRange = (value, minValue, maxValue) =>
  Math.max(minValue, Math.min(maxValue, value));

/**
 * Function to load a key from localStorage and parse it as JSON.
 * If the key does not exist or parsing fails, returns the provided default value.
 * 
 * @param {string} key - The localStorage key to load.
 * @param {*} defaultValue - The default value to return if the key does not exist or parsing fails.
 * 
 * @return {*} - The parsed value from localStorage or the default value.
 */
export const loadFromLocalStorage = (key, defaultValue='') => {

  if (globalThis.window === undefined || !globalThis.localStorage) {
    return defaultValue; // Return default if localStorage is not available (e.g., during SSR)
  }

  try {
    const storedValue = globalThis.localStorage.getItem(key);
    return storedValue ? JSON.parse(storedValue) : defaultValue;
  }
  catch (error) {
    console.error(`Error loading key "${key}" from localStorage:`, error);
    return defaultValue;
  }
};

/** 
 * Save a value to localStorage as a JSON string.
 * 
 * @param {string} key - The localStorage key to save.
 * @param {*} value - The value to save. It will be stringified as JSON.
 * 
 * @return {void}
 */
export const saveToLocalStorage = (key, value) => {

  if (!globalThis.window?.localStorage) {
    return; // Do nothing if localStorage is not available (e.g., during SSR)
  }

  try {
    const stringValue = JSON.stringify(value);
    globalThis.window.localStorage.setItem(key, stringValue);
  }
  catch (error) {
    console.error(`Error saving key "${key}" to localStorage:`, error);
  }
};

/**
 * Remove a key from localStorage.
 * Included for completeness.
 * 
 * @param {string} key 
 */
export const removeFromLocalStorage = (key) => {

  if (!globalThis.window?.localStorage) {
    return; // Do nothing if localStorage is not available (e.g., during SSR)
  }

  globalThis.window.localStorage.removeItem(key);
};

/**
 * Build an abbreviation from a name string using sequential initials.
 *
 * Example:
 *  - "Digital Front End" (max=3) -> "DFE"
 *  - "disQo Dashboard" (max=2) -> "DD"
 *  - "Cardiff" (max=3) -> "CAR"
 *
 * @param {string} name
 * @param {number} [max=2]
 * @returns {string}
 */
export function makeAbbreviation(name, max = 2) {

  if (!name) {
    return 'X';
  }

  const tokens = String(name).trim().split(/[\s._-]+/).filter(Boolean); // Split the string into tokens based on whitespace and common delimiters

  if (tokens.length === 0) {
    return 'X';
  }

  const isAlphaNum = (ch) => /[\p{L}\p{N}]/u.test(ch);
  const toChars = (s) => Array.from(s).filter(isAlphaNum);

  // Single token: take first N chars 
  if (tokens.length === 1) {
    const out = toChars(tokens[0]).slice(0, max).join('');
    return out ? out.toUpperCase().replaceAll(/[^\p{L}\p{N}]/gu, '') : 'X';
  }

  // Multi-token: sequential initials, then pad from last token
  const initials = tokens.map(token => toChars(token)[0]).filter(Boolean);  // Get the first character of each token
  let parts = initials.slice(0, max);

  const need = max - parts.length;
  if (need > 0) { // Pad from last token if we don't have enough initials
    const pad = toChars(tokens.at(-1)).slice(1, 1 + need);
    parts = parts.concat(pad);
  }

  const cleaned = parts.join('').toUpperCase().replaceAll(/[^\p{L}\p{N}]/gu, ''); // Remove any non-alphanumeric characters

  return cleaned || 'X';  // Fallback to 'X' if the result is empty
}


/**
 * Retrieves a value from an object using a dot-separated path string.
 * E.g. getNestedValue(obj, 'a.b.c') → obj.a.b.c
 *
 * @param {Object} obj
 * @param {string} path
 * @returns {any}
 */
export function getNestedValue(obj, path) {
  if (!obj || typeof path !== 'string') {
    return undefined;
  }

  return path.split('.').reduce((acc, key) => {
    return acc[key] === undefined ? undefined : acc[key];
  }, obj);
}

/**
 * Returns AI analysis info for a result item if the analysis is complete.
 * @param {Object} aiMap - AI map keyed by result_item_id from test_result_details
 * @param {string} resultItemId
 * @returns {{ headline: string } | null}
 */
export const getAiAnalysis = (aiMap, resultItemId) => {
  if (!aiMap || !resultItemId) return null;
  const entry = aiMap[resultItemId];
  if (!entry || entry.status !== 'completed') return null;
  return { headline: entry.headline };
};